import { HeaderComponent } from 'components/header/component'
import { RasaContext } from 'context'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import React from 'react'
import Select from 'react-select'
import { Dataset, DatasetParam } from '../../generic/dataset'
import { Loading } from '../loading'
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap'
import { AjaxWrapper, HttpMethod } from "../../generic/ajaxWrapper"
import * as Constants from '../template-library/constants'
import * as GenericRedux from '../../generic/genericRedux'
import { removeExtension } from "../template-library/utils"
import * as Flash from 'components/flash'
import './_styles.scss'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'

interface CopyTemplatesState {
  communityId: string
  isLoading: boolean
  isTemplateLoading: boolean
  selectedCommunityId: any
  templates: any[]
  selectedTemplate: any
  newsletters: any[]
  isNewslettersLoading: boolean
  showConfirmModal: boolean
}

const COPY_SUCCESS = 'Template copied successfully!'
const COPY_FAILURE = 'Failed to copy template. Please try again.'

class CopyTemplatesComponent extends RasaReactComponent<any, CopyTemplatesState> {
  public static contextType = RasaContext
  private sharedStore: SharedStore
  private newsletterSearchTypingTimer: any
  private newsletterSearchTypingTimerInterval = 500 // .5 seconds
  constructor(props: any) {
    super(props, '', {
      communityId: '',
      isLoading: false,
      isTemplateLoading: false,
      selectedCommunityId: null,
      templates: [],
      selectedTemplate: null,
      newsletters: [],
      isNewslettersLoading: false,
      showConfirmModal: false
    })
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    this.sharedStore.getValue(SharedKeys.activeCommunity)
    .then((activeCommunity) => {
      this.setState({
        communityId: activeCommunity.communityId,
      }, () => {
        this.loadNewsletters()
      })
    })
  }

  public render() {
    return (
      <div className="copy-templates-wrapper">
        <div className="header-container">
          <HeaderComponent
            title={'Copy Templates'}
            subTitle={'Template Management'}
            description={[
              'Copy template from source newsletter to current newsletter.',
            ]}/>
        </div>
        {this.state.isLoading ? <Loading size="64"/> :
          <div className="ct-form-wrap">
            <div className="ct-form-row">
              <label>Select Source Newsletter</label>
              <Select
                options={this.state.newsletters.map((newsletter) => ({
                  label: newsletter.display_name || newsletter.community_name || 'Your Newsletter',
                  value: newsletter.community_identifier,
                }))}
                value={this.state.selectedCommunityId}
                onChange={(e) => this.onNewsletterChange(e)}
                onInputChange={this.onNewsletterInputChange}
                placeholder="Search newsletter..."
                noOptionsMessage={() => 'No newsletters found'}
                isLoading={this.state.isNewslettersLoading}
                isSearchable={true}
                filterOption={() => true}
              />
            </div>
            <div className="ct-form-row">
              <label>Select Source Template</label>
              {this.state.isTemplateLoading ? <Loading size="32"/> :
              <Select
                placeholder="Select template..."
                options={this.state.templates.map((template) => {
                  return {
                    label: template.fileName,
                    value: template.id,
                  }
                })}
                value={this.state.selectedTemplate}
                onChange={(e) => this.onTemplateChange(e)}
                disabled={!this.state.selectedCommunityId}
              />}
              {this.state.selectedCommunityId && this.state.templates.length === 0 && (
                <div className="no-templates-message">
                  No templates available for this newsletter
                </div>
              )}
            </div>
            <div className="ct-form-row ct-btn-wrap">
              <span className="buttons">
                <Button
                  onClick={() => this.setState({ showConfirmModal: true })}
                  disabled={!this.state.selectedTemplate || !this.state.selectedCommunityId}
                >
                  Copy Template
                </Button>
              </span>
            </div>
          </div>
        }
        {this.renderConfirmModal()}
      </div>
    )
  }


  private loadNewsletters = (searchValue?: string) => {
    this.setState({ isNewslettersLoading: true })

    const params: DatasetParam[] = []
    if (searchValue && searchValue.trim()) {
      params.push({ param: 'filter', value: searchValue.trim() })
    }

    return new Dataset().loadCommunityDataset('userNewsletters', this.state.communityId, params, 25)
      .then((newsletters) => {
        const filteredNewsletters = newsletters[0].filter(
          newsletter => newsletter.community_identifier !== this.state.communityId
        )
        this.setState({
          newsletters: filteredNewsletters,
          isNewslettersLoading: false
        })
      })
      .catch(() => {
        this.setState({
          newsletters: [],
          isNewslettersLoading: false
        })
      })
  }

  private onNewsletterInputChange = (inputValue: string) => {
    clearTimeout(this.newsletterSearchTypingTimer)

    this.newsletterSearchTypingTimer = setTimeout(() => {
      this.loadNewsletters(inputValue)
    }, this.newsletterSearchTypingTimerInterval)
  }

  private onNewsletterChange = (selected: any) => {
    const communityId = selected.value
    this.setState({
      selectedCommunityId: selected,
      selectedTemplate: null,
      templates: []
    }, () => {
      if (communityId) {
        this.loadEmailTemplates(communityId)
      }
    })
  }

  private onTemplateChange = (selected: any) => {
    this.setState({ selectedTemplate: selected })
  }

  private loadEmailTemplates = (communityId: string) => {
    this.setState({ isTemplateLoading: true })
    const params: DatasetParam[] = [
      { param: 'load_stripo', value: '1' },
      { param: 'layout_type', value: 'newsletter' }
    ]
    return new Dataset().loadCommunityDataset('htmlTemplates', communityId, params)
      .then((htmlTemplates) => {
        // Filter only V2 templates
        const templates = htmlTemplates[0].filter(x => x.type === Constants.TEMPLATE_VERSION.V2)
        templates.map((x) => {
          x.id = removeExtension(x.id)
          x.fileName = removeExtension(x.fileName)
          return x
        })
        this.setState({
          templates,
          isTemplateLoading: false
        })
      })
      .catch(() => {
        this.setState({ isTemplateLoading: false })
      })
  }

  private copyTemplate = () => {
    const selectedCommunityId = this.state.selectedCommunityId.value
    const selectedTemplate = this.state.selectedTemplate.value
    const template = this.state.templates.find(t => t.id === selectedTemplate)

    if (!template) return

    this.setState({ isLoading: true, showConfirmModal: false })

    const url = `${AjaxWrapper.getServerUrl()}/${this.state.communityId}/template/copy`
    AjaxWrapper.ajax(url, HttpMethod.POST, {
      templateId: template.id,
      path: `${template.fileName}`,
      copyFromCommunity: selectedCommunityId,
    })
      .then(() => {
        this.context.store.dispatch(Flash.showFlashMessage(COPY_SUCCESS))
        this.setState({
          isLoading: false
        })
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError(COPY_FAILURE))
        this.setState({ isLoading: false })
      })
  }

  private onModalClose = () => {
    this.setState({ showConfirmModal: false })
  }

  private renderConfirmModal = () => {
    return (
      <Modal isOpen={this.state.showConfirmModal}>
        <ModalBody className="delete-modal">
          <div>
            <h4>Confirm Copy</h4>
            <p>
              Are you sure you want to copy the template{' '}
              <strong>{this.state.selectedTemplate ? this.state.selectedTemplate.label : 'this template'}</strong>{' '}
              from{' '}
              <strong>{this.state.selectedCommunityId ? this.state.selectedCommunityId.label : 'the selected newsletter'}</strong>?
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.copyTemplate} disabled={this.state.isLoading}>
            {this.state.isLoading ? 'Copying...' : 'Copy Template'}
          </Button>
          <Button color="secondary" onClick={this.onModalClose} disabled={this.state.isLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }
}

export const CopyTemplates = GenericRedux.registerNewComponent<any>(
  CopyTemplatesComponent,
  'copy_templates',
  {})
